import React, { Component, Fragment } from 'react'
import { InputForm } from 'app/assets/styles'
import type { CouponForm } from 'shared/types'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import styled from 'styled-components'
import { colors as oldColors } from 'shared/assets/styles'
import { colors } from 'app-v2/design-systems/theme'
import { fonts, styleH6, Text } from 'app/common/typography'
import { Spinner } from 'app/assets/icons'
import { media } from 'app/assets/styles/breakpoints'
import { Recaptcha } from 'app/common/recaptcha'
import { COLOR } from 'app-v2/design-tokens'

const { flameRegular } = fonts
const { red } = oldColors

type Props = {
    addCoupon: CouponForm => void,
    fetchBlazonsPageData: () => void,
    reset: () => void,
    addCouponSuccess: boolean,
    addCouponError: ?string,
    className?: string,
    intl: IntlShape,
    shouldFetchBlazonsOnSuccess?: boolean,
    titleKey?: string,
    pickUpType: string,
    withoutPickUpType?: boolean
}

type State = {
    value: ?string,
    isSubmitting: boolean,
    showCaptcha: boolean
}

class FormCoupon extends Component<Props, State> {
    recaptchaPromise: Promise<string>
    resolve: any
    reject: any

    constructor() {
        super()
        this.state = {
            value: '',
            isSubmitting: false,
            showCaptcha: false
        }
        this.initRecaptchaPromise()
    }

    initRecaptchaPromise = () => {
        this.recaptchaPromise = new Promise((resolve, reject) => {
            this.resolve = resolve
            this.reject = reject
        })
    }

    verifyCallback = (recaptchaToken: string) => {
        if (!!recaptchaToken) {
            this.resolve(recaptchaToken)
        } else {
            this.reject('Error getting recaptcha token')
        }
    }

    handleChange = (e: any) => {
        const { target } = e
        if (target) {
            const { value } = target
            this.setState({ value })
        }
    }

    componentDidUpdate() {
        const {
            addCouponSuccess,
            addCouponError,
            fetchBlazonsPageData,
            shouldFetchBlazonsOnSuccess = false
        } = this.props
        const { isSubmitting } = this.state
        if (isSubmitting && (addCouponSuccess || addCouponError)) {
            this.setState({ isSubmitting: false })
        }
        if (addCouponSuccess && shouldFetchBlazonsOnSuccess) {
            fetchBlazonsPageData()
        }
    }

    componentWillUnmount() {
        const { reset } = this.props
        reset()
    }

    submit = async (e: any) => {
        e.preventDefault()
        const { value, isSubmitting } = this.state
        const trimmedValue = value && value.trim()
        try {
            if (trimmedValue && !isSubmitting) {
                this.setState({ isSubmitting: true, showCaptcha: true })
                const recaptchaToken = await this.recaptchaPromise
                const {
                    reset,
                    addCoupon,
                    pickUpType,
                    withoutPickUpType
                } = this.props

                reset()
                withoutPickUpType
                    ? addCoupon({
                          code: trimmedValue,
                          queen: recaptchaToken
                      })
                    : addCoupon({
                          code: trimmedValue,
                          pickUpType,
                          queen: recaptchaToken
                      })
            }
        } finally {
            this.setState({ isSubmitting: false, showCaptcha: false })
            this.initRecaptchaPromise()
        }
    }

    render() {
        const {
            addCouponSuccess,
            addCouponError,
            className,
            titleKey = 'kingdom.privileges.form.title',
            intl
        } = this.props
        const { value, isSubmitting, showCaptcha } = this.state

        return (
            <div>
                <Form onSubmit={this.submit} className={className}>
                    {showCaptcha && (
                        <Recaptcha
                            action="WebKDCouponForm"
                            verifyCallback={this.verifyCallback}
                        />
                    )}
                    <Infos>
                        <FormattedMessage id={titleKey}>
                            {(text: string) => (
                                <StyledText color={COLOR.SECONDARY}>
                                    {text}
                                </StyledText>
                            )}
                        </FormattedMessage>
                    </Infos>
                    <InputForm className="dashboard">
                        <Input
                            type="text"
                            placeholder={intl.formatMessage({
                                id: 'kingdom.privileges.form.placeholder'
                            })}
                            value={value}
                            onChange={this.handleChange}
                        />
                        <FormattedMessage id="footer.newsletter.submit">
                            {(label: string) => (
                                <StyledButton
                                    type="submit"
                                    disabled={isSubmitting}
                                    color={COLOR.PRIMARY}>
                                    {label}
                                </StyledButton>
                            )}
                        </FormattedMessage>
                    </InputForm>
                </Form>
                {isSubmitting ? (
                    <SpinnerContainer>
                        <Spinner size={40} />
                    </SpinnerContainer>
                ) : (
                    <Fragment>
                        {addCouponSuccess && (
                            <Mentions>
                                <FormattedMessage id="kingdom.privileges.form.success">
                                    {(text: string) => (
                                        <Text color={colors.crunchyGreen}>
                                            {text}
                                        </Text>
                                    )}
                                </FormattedMessage>
                            </Mentions>
                        )}
                        {addCouponError && (
                            <Mentions>
                                <FormattedMessage id={addCouponError}>
                                    {(text: string) => (
                                        <Text color={red}>{text}</Text>
                                    )}
                                </FormattedMessage>
                            </Mentions>
                        )}
                    </Fragment>
                )}
            </div>
        )
    }
}

const Form = styled.form`
    padding: 15px;
    border-radius: 10px;
    background: ${COLOR.PRIMARY};
    min-width: 300px;

    &.dashboard {
        width: 100%;
        margin: 15px 0;
        ${media.medium`
            width: calc(50% - 15px);
        `};
    }
`

const Infos = styled.div`
    text-align: center;
`

const StyledText = styled(Text)`
    font-family: ${flameRegular};
`

const Mentions = styled.div`
    margin-top: 25px;
`

const StyledButton = styled.button`
    ${styleH6};
    background: none;
    border: none;
    cursor: ${props => (props.disabled ? 'none' : 'pointer')};
    position: absolute;
    z-index: 2;
    padding-top: 8px;
    top: 50%;
    transform: translateY(-50%);
    left: calc(100% - 50px);
`

const Input = styled.input``

const SpinnerContainer = styled.div`
    margin-top: 25px;
    display: flex;
    justify-content: center;
    align-items: center;
`

export const FormCouponComponent = injectIntl(FormCoupon)
